import lodashCompact from 'lodash-es/compact';
import lodashKebabCase from 'lodash-es/kebabCase';
import lodashSnakeCase from 'lodash-es/snakeCase';

import { SellingMethod } from '~/apiClient';
import { EMPTY_OPTION_VALUE } from '~/constants/filters';
import { ROUTE_NAMES } from '~/constants/routes';
import { Locale } from '~i18n/i18nConfig';

export type ListNameSource = 'page' | 'related';

export const formatNumber = (n: number): string => n.toLocaleString('en-US');

export const formatMileage = (km: number): string => {
    const thresholds = [50_000, 100_000, 200_000, 500_000, 1_000_000];

    if (km < thresholds[0]) {
        return `less than ${formatNumber(thresholds[0])} km`;
    }

    for (let i = 0; i < thresholds.length - 1; i++) {
        const lower = thresholds[i];
        const upper = thresholds[i + 1];

        if (km >= lower && km < upper) {
            return `from ${formatNumber(lower)} to ${formatNumber(upper)} km`;
        }
    }

    return `over ${formatNumber(thresholds[thresholds.length - 1])} km`;
};

export const formatTimeRemaining = (date: string): string => {
    const { timeSyncNow } = useTimeSync();
    const dayjs = useDayjs();

    const remainingSeconds = Math.max(Math.round((Date.parse(date) - timeSyncNow.value) / 1000), 0);

    if (remainingSeconds === 0) {
        return 'zero';
    }

    const thresholds = [
        dayjs.duration({ minutes: 30 }),
        dayjs.duration({ hours: 1 }),
        dayjs.duration({ hours: 3 }),
        dayjs.duration({ days: 1 }),
        dayjs.duration({ days: 3 }),
    ];

    const secondsThresholds = thresholds.map(d => d.asSeconds());

    if (remainingSeconds < secondsThresholds[0]) {
        return `less than ${thresholds[0].humanize()}`;
    }

    for (let i = 0; i < secondsThresholds.length - 1; i++) {
        const lower = secondsThresholds[i];
        const upper = secondsThresholds[i + 1];

        if (remainingSeconds >= lower && remainingSeconds < upper) {
            return `from ${thresholds[i].humanize()} to ${thresholds[i + 1].humanize()}`;
        }
    }

    return `over ${thresholds[thresholds.length - 1].humanize()}`;
};

export const formatTrackingListName = (source: ListNameSource): string | null => {
    if (source === 'related') return 'related_vehicles';

    const route = useRoute();
    const routeBaseName = useRouteBaseName();
    const routeName = routeBaseName(route);

    if (routeName === ROUTE_NAMES.ACCOUNT_BIDS) return 'your_bids';
    if (routeName === ROUTE_NAMES.ACCOUNT_YOUR_AUCTIONS) return 'your_auctions';
    if (routeName === ROUTE_NAMES.ACCOUNT_YOUR_DIRECT_SALES) return 'your_buy_now';
    if (routeName === ROUTE_NAMES.ACCOUNT_FAVORITES) return 'favorites';

    if (isSlpRoute(routeName)) {
        const sellingMethod = getSellingMethodFromRouteName(routeName);
        const sellingMethodListNamePart = { [SellingMethod.Auction]: 'auctions', [SellingMethod.DirectSale]: 'direct-sales' }[sellingMethod];
        const slpParsedRouteParamsState = useSlpParsedRouteParamsState(route);
        const { assetType, platformClass, brandUrlKey, modelUrlKey } = slpParsedRouteParamsState.value;
        const localizedEnClass = platformClass && localizedParams[Locale.EN].class[assetType][platformClass];

        return lodashCompact([sellingMethodListNamePart, assetType, localizedEnClass, brandUrlKey, modelUrlKey]).map(lodashKebabCase).join('_');
    }

    return null;
};

export const formatSortType = (sortSelectModel: string): string => {
    const safeJsonParse = useSafeJsonParse();

    if (sortSelectModel === EMPTY_OPTION_VALUE) return 'default_order';

    const parsedValue = safeJsonParse<SortingParams<string>>(sortSelectModel, `tracking formatSortType: ${sortSelectModel}`);
    if (!parsedValue) return 'unknown';
    const { sortBy, sortOrder } = parsedValue;

    return lodashSnakeCase(`${sortBy}_${sortOrder}`);
};
